import React from 'react';
import TextField from '@mui/material/TextField';
import TableCell from '@mui/material/TableCell';
import { IconButton, MenuItem, TableRow } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import {
  CalculationFunctionValuesEnum,
  MechanicStepDefaultValuesType,
  MechanicStepType,
  MechanicStepVisibleColumnsType,
  MechanicRewardTypesEnum,
} from 'entities/mechanic/model';
import { useMechanicStepDisabledRule } from 'entities/mechanic/lib';
import { PrizeType } from 'entities/prize';
import Autocomplete from '@mui/material/Autocomplete';
import { TransformedPrizeListType } from 'entities/mechanic/types';

type MechanicStepTableNumberRowPropsType = {
  editableRow: number | null;
  creatableRow: number | null;
  row: MechanicStepType;
  defaultValues: MechanicStepDefaultValuesType;
  editRow: (idx: number) => void;
  removeItem: (idx: number) => void;
  saveItem: (idx: number, item: MechanicStepType) => void;
  stopEdit: () => void;
  calculationFunction: CalculationFunctionValuesEnum;
  prizeData: PrizeType[];
  CF3_target: boolean;
  CF4_target: boolean;
  CF6_target: boolean;
};

export const MechanicStepTableEditedNumberRow = ({
  defaultValues,
  editableRow,
  creatableRow,
  row,
  stopEdit,
  removeItem,
  saveItem,
  calculationFunction,
  prizeData,
  CF3_target,
  CF4_target,
  CF6_target,
}: MechanicStepTableNumberRowPropsType) => {
  const DEFAULT_COLUMN_WIDTH = CF3_target ? 200 : 100;

  const [currentRow, setCurrentRow] = React.useState<MechanicStepType | null>(row || null);
  const disabledRule = useMechanicStepDisabledRule(currentRow, calculationFunction);

  const transformedPrizeList: TransformedPrizeListType[] =
    prizeData?.map(({ name: { ru }, prize_type_id }: PrizeType) => ({
      label: ru || 'Не задано',
      prize_type_id,
    })) || [];

  const selectedPrize = transformedPrizeList.find((option) =>
    option.prize_type_id === currentRow?.reward
  );

  const handleChangeInput = (idx: number, fieldName: keyof MechanicStepType, value: number | string) => {
    if (currentRow === null) {
      setCurrentRow({ id: idx, [fieldName]: value });
      return;
    }

    setCurrentRow((prevState) => ({ ...prevState, [fieldName]: value, id: idx }));
  };

  const handleChangeInputNumber = (
    idx: number,
    fieldName: keyof MechanicStepVisibleColumnsType,
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    minValue?: number,
    maxValue?: number,
  ) => {
    const { value } = e.target;
    if (fieldName === 'reward_type') {
      handleChangeInput(idx, fieldName, '');
    }

    if (value === '') {
      handleChangeInput(idx, fieldName, value);
      e.target.value = value;
      return;
    }

    if (Number(value) != null && minValue != null && Number(value) < minValue) {
      handleChangeInput(idx, fieldName, minValue);
      e.target.value = `${minValue}`;
      return;
    }

    if (Number(value) != null && maxValue != null && Number(value) > maxValue) {
      handleChangeInput(idx, fieldName, maxValue);
      e.target.value = `${maxValue}`;
      return;
    }

    handleChangeInput(idx, fieldName, value);
    e.target.value = value;
  };

  const handleClickRemoveRow = (idx: number) => {
    removeItem(idx);
    stopEdit();
  };

  return (
    <TableRow>
      <TableCell sx={{ width: DEFAULT_COLUMN_WIDTH }}>
        <TextField
          defaultValue={defaultValues.target?.visibleValue || row.target}
          type={defaultValues.target?.type || 'number'}
          disabled={defaultValues.target?.disabled || false}
          onChange={(event) => handleChangeInputNumber(row.id, 'target', event, 0)}
        />
      </TableCell>
      {CF6_target ? (
        <TableCell sx={{ width: DEFAULT_COLUMN_WIDTH - 100 }}>
          <Autocomplete
            disablePortal
            options={transformedPrizeList}
            sx={{ width: 300 }}
            value={selectedPrize || null}
            onChange={(event, newValue) => {
              if (newValue) {
                setCurrentRow({
                  ...currentRow,
                  id: row.id,
                  reward: newValue?.prize_type_id,
                  reward_type: MechanicRewardTypesEnum.PRIZE,
                });
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Список призов"
              />
            )}
          />
        </TableCell>
      ) : CF3_target || CF4_target ? (
        <>
          <TableCell sx={{ width: DEFAULT_COLUMN_WIDTH - 50 }}>
            <TextField
              select
              value={currentRow?.reward_type || ''}
              label="Тип"
              fullWidth
              onChange={(event) => handleChangeInputNumber(row.id, 'reward_type', event, 0)}
            >
              {CF3_target && <MenuItem value={MechanicRewardTypesEnum.MONEY}>Монеты</MenuItem>}
              <MenuItem value={MechanicRewardTypesEnum.PRIZE}>Приз</MenuItem>
            </TextField>
          </TableCell>
          {currentRow?.reward_type === MechanicRewardTypesEnum.PRIZE ? (
            <TableCell sx={{ width: DEFAULT_COLUMN_WIDTH - 100 }}>
              <Autocomplete
                disablePortal
                options={transformedPrizeList}
                sx={{ width: 300 }}
                value={selectedPrize || null}
                onChange={(event, newValue) => {
                  if (newValue) {
                    setCurrentRow({
                      ...currentRow,
                      reward: newValue?.prize_type_id,
                    });
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Список призов"
                  />
                )}
              />
            </TableCell>
          ) : (
            <TableCell sx={{ width: DEFAULT_COLUMN_WIDTH }}>
              <TextField
                defaultValue={defaultValues.reward?.visibleValue || row.reward}
                type={defaultValues.reward?.type || 'number'}
                disabled={!currentRow?.reward_type}
                onChange={(event) => handleChangeInputNumber(row.id, 'reward', event, 0)}
              />
            </TableCell>
          )}
        </>
      ) : (
        <TableCell sx={{ width: DEFAULT_COLUMN_WIDTH }}>
          <TextField
            defaultValue={defaultValues.reward?.visibleValue || row.reward}
            type={defaultValues.reward?.type || 'number'}
            disabled={defaultValues.reward?.disabled || false}
            onChange={(event) => handleChangeInputNumber(row.id, 'reward', event, 0)}
          />
        </TableCell>
      )}
      <TableCell
        key="table_actions"
        sx={{ width: DEFAULT_COLUMN_WIDTH }}
      >
        <IconButton
          disabled={disabledRule}
          onClick={() => saveItem(row.id, currentRow!)}
        >
          <CheckIcon />
        </IconButton>
        <IconButton onClick={() => (editableRow === creatableRow ? handleClickRemoveRow(row.id) : stopEdit())}>
          <CloseOutlinedIcon />
        </IconButton>
      </TableCell>
    </TableRow>
  );
};
